import { parsePhoneNumberFromString, PhoneNumber, PhoneNumberType } from 'libphonenumber-js/max';

/**
 * Estimage-online doesn't have any phone formatting, only phone validation, which now uses the libphonenumber-js library.
 */

// libphonenumber library allows "ext", "*" and others - so keep that REGEX check
const VALID_CHARS_REGEX = /^[0-9\s()+\-]+$/;

type SupportedRegion = 'AU' | 'NZ';

// In some rare cases, a number could be either AU Landline or NZ Mobile. (ex: 0291234567)
// We define this priority to match for AU numbers first and return the country as AU.
const REGION_PRIORITY: SupportedRegion[] = ['AU', 'NZ'];

// Allowed types from the library for our purposes
const ALLOWED_TYPES: ReadonlyArray<PhoneNumberType> = [
  'MOBILE',
  'FIXED_LINE',
  'FIXED_LINE_OR_MOBILE',
  'TOLL_FREE',
  'SHARED_COST'
];

function isSupportedCountry(parsed: PhoneNumber): boolean {
  return REGION_PRIORITY.includes(parsed.country as SupportedRegion);
}

function parseInternationalNumber(phone: string): PhoneNumber | undefined {
  const parsed = parsePhoneNumberFromString(phone);
  if (parsed && isSupportedCountry(parsed) && parsed.isValid()) {
    return parsed;
  }
  return undefined;
}

function parseNationalNumber(
  phone: string,
  predicate: (p: PhoneNumber) => boolean = () => true
): PhoneNumber | undefined {
  for (const region of REGION_PRIORITY) {
    const parsed = parsePhoneNumberFromString(phone, region);
    if (parsed && isSupportedCountry(parsed) && parsed.isValid() && predicate(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

// Checks if the number is valid AND if it's of an accepted type
function parseAccepted(phone: string): PhoneNumber | undefined {
  if (!phone || !VALID_CHARS_REGEX.test(phone)) {
    return undefined;
  }

  const parsed = parseInternationalNumber(phone) ?? parseNationalNumber(phone);
  if (!parsed) {
    return undefined;
  }

  const type = parsed.getType();
  return type && ALLOWED_TYPES.includes(type) ? parsed : undefined;
}

/**
 * General (non-mobile) validity: a valid, in-scope AU/NZ number of an accepted type.
 */
export function isValidPhoneNumber(phone: string): boolean {
  return !!parseAccepted(phone);
}

/**
 * Mobile validity: a valid, in-scope AU/NZ mobile number.
 */
export function isValidMobilePhoneNumber(phone: string): boolean {
  if (!phone || !VALID_CHARS_REGEX.test(phone)) {
    return false;
  }

  const isMobile = (p: PhoneNumber): boolean => p.getType() === 'MOBILE';

  const international = parseInternationalNumber(phone);
  if (international) {
    return isMobile(international);
  }

  return !!parseNationalNumber(phone, isMobile);
}

/**
 * Returns the country name for a valid, in-scope phone number, or null if the number is invalid or out-of-scope.
 */
export function getPhoneNumberCountry(phone?: string): 'Australia' | 'New Zealand' | null {
  if (!phone) {
    return null;
  }
  const parsed = parseAccepted(phone);
  if (!parsed) {
    return null;
  }
  if (parsed.country === 'AU') {
    return 'Australia';
  }
  if (parsed.country === 'NZ') {
    return 'New Zealand';
  }
  return null;
}
